import { useAppSelector } from '@monorepo/shared';
import {
  selectAccountAllHa,
  selectAccountAllInstances,
  selectAccountHaCreatable
} from '@monorepo/shared/store/haInstanceSlice';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonToolbar,
  FlexboxGrid,
  Form,
  List,
  Loader,
  Message,
  Modal,
  SelectPicker,
  Stack,
  TagPicker
} from 'rsuite';
import { RequiredIcon } from './Provision';
import { t } from 'i18next';
import {
  useCreateHaMutation,
  useLazyGetRegionsHaListQuery
} from '@monorepo/shared/services/regionHa';
import { selectRegions } from '@monorepo/shared/store/authSlice';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';

export const HaCreate = () => {
  const { accountId, haId } = useParams();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const accountRegionHas = useAppSelector(selectAccountAllHa);
  const authRegions = useAppSelector(selectRegions);
  const accountHaCreatable = useAppSelector(selectAccountHaCreatable);
  const accountRegionInstances = useAppSelector(selectAccountAllInstances);
  const [ha, setHa] = useState<Record<string, any>>({});
  const [primaryInstance, setPrimaryInstance] = useState<Record<string, any>>(
    {}
  );
  const [formValue, setFormValue] = useState<Record<string, any>>({
    name: '',
    desc: '',
    region: '',
    instanceIds: []
  });
  const [regions, setRegions] = useState<Record<string, any>[]>([]);
  const [instanceIds, setInstanceIds] = useState<Record<string, any>[]>([]);
  const [message, setMessage] = useState<string>('');
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showAfterInfo, setShowAfterInfo] = useState<boolean>(false);

  const [createHa, createResult] = useCreateHaMutation();
  const [open, setOpen] = useState<boolean>(false);

  const [getRegionHa] = useLazyGetRegionsHaListQuery();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const formNames: Record<string, string> = {
    name: 'Cluster Name',
    desc: 'Notes',
    region: 'Region',
    instanceIds: 'Instances'
  };
  useEffect(() => {
    const has = accountRegionHas?.[accountId || ''];
    if (has?.length && haId) {
      const haObj = has.find((obj: Record<string, any>) => obj.ha_id == haId);
      // const instances = accountRegionInstances?.[accountId || ''];
      // const noHaInstancesList = Object.values(instances).filter(
      //   (obj: Record<string, any>) => !obj.ha_id
      // );
      // if (haObj) {
      //   setHa({
      //     ...haObj,
      //     instances: haObj?.instances
      //       ? [...haObj.instances, ...noHaInstancesList]
      //       : [...noHaInstancesList]
      //   });
      // }
      haObj && setHa(haObj);
      setFormValue({
        name: haObj?.ha_name || '',
        region: haObj?.region_alias || '',
        desc: haObj?.ha_desc || '',
        instanceIds:
          haObj?.instances?.map(
            (obj: Record<string, any>) => obj?.instance_id
          ) || []
      });
    }
  }, [accountRegionHas, haId]);
  useEffect(() => {
    // need to show all regions to options even the region is not cluster creatable
    setRegions(
      Object.keys(authRegions).map((region: string) => ({
        label: authRegions[region]?.name || region,
        value: region
      }))
    );
  }, [authRegions]);
  useEffect(() => {
    const haCreatable = accountHaCreatable?.[accountId || ''];
    const existIds = ha?.instances || [];
    const creatableIds = haCreatable[formValue.region] || [];
    const source = haId ? [...existIds, ...creatableIds] : [...creatableIds];
    // no firmware information can not add to cluster
    const primary = source.find(
      (obj: Record<string, any>) => obj?.cluster_info?.node_role === 'Primary'
    );
    primary?.instance_id && setPrimaryInstance(primary);
    setInstanceIds(source.filter((obj: Record<string, any>) => obj.firmware));
  }, [formValue.region, ha, accountHaCreatable]);

  useEffect(() => {
    if (createResult.error) {
      const data: Record<string, any> = createResult.error;
      const code = data?.data?.error_code || data?.status;
      const message = data?.data?.error_desc || data?.data?.message;
      if (message && setMessage) {
        setMessage(`${message} [Error_code: ${code}]`);
        setShowMessage(true);
      }
    } else if (createResult.isSuccess) {
      if (haId) {
        getRegionHa({
          account_id: currentAccount.account_id,
          user_id: currentAccount.user_id,
          access_from_user_id:
            currentAccount.access_from_user_id ||
            currentAccount.access_from?.user_id,
          regions: Object.values(authRegions)
        });
        setTimeout(() => {
          navigate(-1);
        }, 200);
      } else {
        setShowAfterInfo(true);
      }
    }
  }, [createResult]);

  const updateFormValue = (obj: Record<string, any>) => {
    if (obj?.key && obj.value) {
      setFormValue({ ...formValue, [obj.key]: obj.value });
    }
  };
  const checkError = () => {
    setMessage('');
    const messages: Array<string> = [];
    if (!formValue.name) {
      messages.push('Cluster name can not be empty');
    }
    if (!formValue.region) {
      messages.push('Please select a region');
    }
    // Not Allow create cluster without instance
    if (!haId && (!formValue.instanceIds || formValue.instanceIds.length < 2)) {
      messages.push('Must have at least two instance members');
    } else {
      const versions = instanceIds
        .filter((inst: Record<string, any>) =>
          formValue.instanceIds.includes(inst.instance_id)
        )
        ?.map((inst: Record<string, any>) => inst.firmware);
      if (!versions.every((v) => v === versions?.[0])) {
        messages.push('Only same version instances are allowed');
      }
    }

    // bug: 1056155 - User should not be able to remove primary instance from cluster
    if (haId && primaryInstance?.instance_id) {
      if (!formValue.instanceIds.includes(primaryInstance?.instance_id)) {
        messages.push('Not Allow to remove primary instance from Cluster');
      }
    }

    if (messages.length) {
      setShowMessage(true);
      setMessage(messages.join('|'));
    } else {
      setShowMessage(false);
      handleOpen();
    }
  };

  const confirm = () => {
    createHa({
      region: authRegions[formValue.region],
      ha_id: haId,
      data: {
        account_id: accountId,
        ha_name: formValue.name,
        ha_desc: formValue.desc,
        instanceIds: formValue.instanceIds
      }
    });
    handleClose();
  };

  return (
    <div className="ha-detail-container">
      {createResult.isLoading ? (
        <div style={{ height: '90%', position: 'relative' }}>
          <Loader center size="md" />
        </div>
      ) : showAfterInfo ? (
        <div style={{ height: '90%', position: 'relative' }}>
          <h4>{'Cluster creation process initiated successfully!'} </h4>
          <p>
            {
              'As a next step, we will setup the required background services and inform you about the cluster activation on your registered email.'
            }
          </p>
          <ButtonToolbar style={{ margin: '130px', justifyContent: 'center' }}>
            <Button
              appearance="ghost"
              className="action-btn"
              onClick={() => {
                haId ? navigate(-2) : navigate(-1);
              }}
            >
              Close
            </Button>
          </ButtonToolbar>
        </div>
      ) : (
        <>
          {showMessage ? (
            <Message
              showIcon
              type="error"
              header="A problem occurred:"
              style={{ marginBottom: '20px' }}
            >
              <ul>
                {message.split('|')?.map((str: string, index: number) => (
                  <li key={index}>{str}</li>
                ))}
              </ul>
            </Message>
          ) : (
            <></>
          )}
          <Form fluid formValue={formValue} onChange={setFormValue}>
            <Form.Group controlId="name" aria-required>
              <Form.ControlLabel>
                {formNames.name}
                <RequiredIcon />
              </Form.ControlLabel>
              <Form.Control name="name" readOnly={!!haId} />
            </Form.Group>
            <Form.Group controlId="region">
              <Form.ControlLabel>
                {formNames.region}
                <RequiredIcon />
              </Form.ControlLabel>
              <div>
                <SelectPicker
                  disabled={!!haId}
                  searchable={false}
                  style={{ width: '100%' }}
                  value={formValue.region}
                  data={regions}
                  cleanable={false}
                  onChange={(val: string | null) => {
                    updateFormValue({ key: 'region', value: val });
                  }}
                />
              </div>
            </Form.Group>
            <Form.Group controlId="instanceIds">
              <Form.ControlLabel>
                {formNames.instanceIds}
                {/* <RequiredIcon /> */}
              </Form.ControlLabel>
              <div>
                <TagPicker
                  data={instanceIds}
                  groupBy="firmware"
                  valueKey="instance_id"
                  labelKey="license_sn"
                  value={formValue.instanceIds}
                  style={{ width: '100%' }}
                  onChange={(ids: string[]) => {
                    updateFormValue({ key: 'instanceIds', value: ids });
                  }}
                />
              </div>
            </Form.Group>
            <Form.Group controlId="desc" aria-required>
              <Form.ControlLabel>{formNames.desc}</Form.ControlLabel>
              <Form.HelpText>
                Please provide any additional information or specific
                instructions that you would like to share with the support team
                regarding the HA cluster creation. This may include special
                requirements if any.
              </Form.HelpText>
              <Form.Control name="desc" />
            </Form.Group>
            <ButtonToolbar>
              <Button
                appearance="ghost"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
              <Button appearance="primary" onClick={checkError}>
                {'Submit'}
              </Button>
            </ButtonToolbar>
          </Form>
          <Modal open={open} onClose={handleClose} size="md">
            <Modal.Header>
              <Modal.Title>
                {t('common.provision.confirmDialog.title')}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="info-list">
                <h6 style={{ marginBottom: '10px' }}></h6>
                <List>
                  {Object.keys(formValue).map((key: string, index: number) => {
                    return (
                      <List.Item key={key}>
                        <FlexboxGrid>
                          {/*icon*/}
                          <FlexboxGrid.Item colspan={6}>
                            {formNames[key]}
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item colspan={16}>
                            {key === 'instanceIds' ? (
                              <ul style={{ paddingLeft: '12px' }}>
                                {formValue[key]?.map((v: string, i: number) => {
                                  const name = instanceIds.find(
                                    (o: Record<string, any>) =>
                                      o.instance_id === v
                                  )?.license_sn;
                                  return <li key={i + 1}>{name}</li>;
                                })}
                              </ul>
                            ) : key === 'region' ? (
                              authRegions[formValue[key]]?.name ||
                              formValue[key]
                            ) : (
                              formValue[key]
                            )}
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      </List.Item>
                    );
                  })}
                </List>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={handleClose} appearance="ghost">
                Cancel
              </Button>
              <Button onClick={confirm} appearance="primary">
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};
